import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
	Outlet,
	RouterProvider,
	createRootRoute,
	createRoute,
	createRouter,
} from "@tanstack/react-router";
import { createRoot } from "react-dom/client";

// Import workspace packages
import { ThemeProvider, useTheme } from "@repo/forms-ui";
import type React from "react";
import { RequireAuth } from "./components/auth/RequireAuth";
import "./styles/theme.css";

// Import routes
import FormBuilderRoute from "./routes/forms/$org/$slug/builder";
import FormResponsesRoute from "./routes/forms/$org/$slug/responses";
import Index from "./routes/index";
import PortalEditRoute from "./routes/portals/$org/$slug/edit";
import PortalPreviewRoute from "./routes/portals/$org/$slug/preview";

// Create root route
const rootRoute = createRootRoute({
	component: () => (
		<RequireAuth>
			<Outlet />
		</RequireAuth>
	),
});

// Define routes
const indexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/",
	component: Index,
});

const formBuilderRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/forms/$org/$slug/builder",
	component: FormBuilderRoute,
});

const formResponsesRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/forms/$org/$slug/responses",
	component: FormResponsesRoute,
});

const portalPreviewRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/portals/$org/$slug/preview",
	component: PortalPreviewRoute,
});

const portalEditRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/portals/$org/$slug/edit",
	component: PortalEditRoute,
});

// Create route tree
const routeTree = rootRoute.addChildren([
	indexRoute,
	formBuilderRoute,
	formResponsesRoute,
	portalPreviewRoute,
	portalEditRoute,
]);

// Create router
const router = createRouter({
	routeTree,
	basepath: "/",
});

// TypeScript declarations
declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

// Configure React Query
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 1000 * 60 * 5, // 5 minutes
			gcTime: 1000 * 60 * 10, // 10 minutes
			retry: (failureCount, error) => {
				// Don't retry on 4xx errors
				if (error instanceof Error && error.message.includes("4")) {
					return false;
				}
				return failureCount < 3;
			},
		},
	},
});

// Error boundary component
function ErrorBoundary({ error }: { error: Error }) {
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#1a0f0f" }}>
      <div className="card" style={{ maxWidth: 520, width: "100%", borderColor: "#7f1d1d" }}>
        <div className="card-body" style={{ display: "grid", gap: 10 }}>
          <h2 style={{ margin: 0, color: "#fecaca" }}>Something went wrong</h2>
          <p style={{ margin: 0, color: "#fca5a5" }}>{error.message}</p>
          <button type="button" className="btn" onClick={() => window.location.reload()}>
            Reload Page
          </button>
        </div>
      </div>
    </div>
  );
}

function Chrome({ children }: { children: React.ReactNode }) {
  const { mode, setMode, accent, setAccent } = useTheme();
  const onAccentChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
    const v = e.target.value as "violet" | "blue" | "green" | "orange";
    setAccent(v);
  };
  return (
    <div className="container">
      <header className="app-header">
        <div style={{ display: "grid", gap: 6 }}>
          <span className="pill">Builder</span>
          <strong style={{ fontSize: 18, letterSpacing: "-0.01em" }}>Attorney Forms Builder</strong>
          <span className="muted">Design, preview, and publish with confidence</span>
        </div>
        <div className="toolbar">
          <button type="button" className="btn ghost" onClick={() => setMode(mode === "light" ? "dark" : "light")}>{mode === "light" ? "☾ Dark" : "☀︎ Light"}</button>
          <select aria-label="Accent" value={accent} onChange={onAccentChange} className="btn ghost" style={{ padding: "10px 12px" }}>
            <option value="violet">Violet</option>
            <option value="blue">Blue</option>
            <option value="green">Green</option>
            <option value="orange">Orange</option>
          </select>
          <a className="btn" href="/portals/demo/client-portal/preview">Preview Portal</a>
        </div>
      </header>
      {children}
    </div>
  );
}

// Main App component
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Chrome>
          <RouterProvider router={router} defaultErrorComponent={ErrorBoundary} />
        </Chrome>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

// Mount the app
const el = document.getElementById("root");
if (!el) throw new Error("Root element not found");
createRoot(el).render(<App />);

export default App;
